import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { taskService } from '../services/taskService'
import { userService } from '../services/userService'
import { patientService } from '../services/patientService'
import { auditService } from '../services/auditService'
import { Task, TaskStatus } from '../types/task'
import { TaskForm } from '../components/TaskForm'
import { formatUserName } from '../utils/name'
import { formatDate } from '../utils/locale'
import './TaskDetail.css'

export function TaskDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)

  const { data: task, isLoading, error } = useQuery({
    queryKey: ['task', id],
    queryFn: () => taskService.getById(id!),
    enabled: !!id,
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
  })

  const { data: patient } = useQuery({
    queryKey: ['patient', task?.patientId],
    queryFn: () => patientService.getById(task!.patientId!),
    enabled: !!task?.patientId,
  })

  const { data: auditHistory } = useQuery({
    queryKey: ['audit', 'task', id],
    queryFn: () => {
      if (!task?.patientId) return Promise.resolve({ entries: [], count: 0 })
      return auditService.getPatientAuditHistory(task.patientId, {
        limit: 50,
      })
    },
    enabled: !!task?.patientId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Task>) => taskService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', id] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => taskService.delete(id!),
    onSuccess: () => {
      navigate('/tasks')
    },
  })

  const statusChangeMutation = useMutation({
    mutationFn: ({ status, reason }: { status: TaskStatus; reason?: string }) =>
      taskService.updateStatus(id!, status, reason),
    onMutate: async ({ status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['task', id] })
      
      // Snapshot the previous value
      const previousTask = queryClient.getQueryData<Task>(['task', id])
      
      // Optimistically update to the new value
      if (previousTask) {
        queryClient.setQueryData<Task>(['task', id], {
          ...previousTask,
          status,
          updatedAt: new Date().toISOString(),
          startedAt: status === 'in-progress' ? new Date().toISOString() : previousTask.startedAt,
          completedAt: status === 'completed' || status === 'resolved' ? new Date().toISOString() : previousTask.completedAt,
        })
      }
      
      // Return a context object with the snapshotted value
      return { previousTask }
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousTask) {
        queryClient.setQueryData(['task', id], context.previousTask)
      }
    },
    onSuccess: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['task', id] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const assignMutation = useMutation({
    mutationFn: (userId: string) => taskService.assign(id!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', id] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const unassignMutation = useMutation({
    mutationFn: () => taskService.unassign(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', id] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const handleUpdate = (formData: any) => {
    updateMutation.mutate(formData)
  }

  const handleStatusChange = (status: TaskStatus, reason?: string) => {
    if (window.confirm(`Change task status to ${status}?`)) {
      statusChangeMutation.mutate({ status, reason })
    }
  }

  const handleAssign = (userId: string) => {
    assignMutation.mutate(userId)
  }

  const handleUnassign = () => {
    if (window.confirm('Unassign this task?')) {
      unassignMutation.mutate()
    }
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      deleteMutation.mutate()
    }
  }

  const getUserName = (userId?: string) => {
    if (!userId) return 'Unassigned'
    const user = users.find((u) => u.id === userId)
    return user ? formatUserName(user as any) : userId
  }

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'created':
      case 'assigned':
      case 'in-progress':
        return '#2563eb'
      case 'performed':
      case 'resolved':
      case 'completed':
        return '#16a34a'
      case 'held':
      case 'pended':
        return '#f59e0b'
      case 'denied':
        return '#dc2626'
      case 'cancelled':
        return '#6b7280'
      case 'unassigned':
        return '#9ca3af'
      default:
        return '#6b7280'
    }
  }

  if (isLoading) {
    return <div className="task-detail-loading">Loading task...</div>
  }

  if (error || !task) {
    return <div className="task-detail-error">Task not found</div>
  }

  const taskAuditEntries = auditHistory?.entries?.filter(
    (entry) => entry.entityType === 'task' && entry.entityId === id
  ) || []

  return (
    <div className="task-detail">
      <div className="task-detail-header">
        <button onClick={() => navigate('/tasks')} className="btn-back">
          ← Back to Tasks
        </button>
        <div className="task-detail-actions">
          {!isEditing ? (
            <>
              <button onClick={() => setIsEditing(true)} className="btn-edit">
                Edit
              </button>
              <button onClick={handleDelete} className="btn-delete">
                Delete
              </button>
            </>
          ) : (
            <button onClick={() => setIsEditing(false)} className="btn-cancel">
              Cancel
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="task-detail-edit">
          <TaskForm
            initialData={task}
            onSubmit={handleUpdate}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      ) : (
        <>
          <div className="task-detail-main">
            <div className="task-detail-section">
              <h2>{task.title}</h2>
              <div className="task-detail-meta">
                <div className="task-meta-item">
                  <span className="task-meta-label">Status:</span>
                  <span
                    className="task-status-badge"
                    style={{ backgroundColor: getStatusColor(task.status) }}
                  >
                    {task.status}
                  </span>
                </div>
                <div className="task-meta-item">
                  <span className="task-meta-label">Priority:</span>
                  <span className={`task-priority-badge priority-${task.priority}`}>
                    {task.priority}
                  </span>
                </div>
                {task.urgency && (
                  <div className="task-meta-item">
                    <span className="task-meta-label">Urgency:</span>
                    <span className="task-urgency-badge">{task.urgency}</span>
                  </div>
                )}
              </div>

              {task.description && (
                <div className="task-detail-description">
                  <h3>Description</h3>
                  <p>{task.description}</p>
                </div>
              )}

              {task.notes && (
                <div className="task-detail-notes">
                  <h3>Notes</h3>
                  <p>{task.notes}</p>
                </div>
              )}

              <div className="task-detail-info">
                <div className="task-info-row">
                  <span className="task-info-label">Assigned To:</span>
                  <span className="task-info-value">
                    {task.assignedTo ? (
                      <>
                        {getUserName(task.assignedTo)}
                        <button
                          onClick={handleUnassign}
                          className="btn-unassign"
                          style={{ marginLeft: '0.5rem' }}
                        >
                          Unassign
                        </button>
                      </>
                    ) : (
                      <>
                        Unassigned
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              handleAssign(e.target.value)
                            }
                          }}
                          style={{ marginLeft: '0.5rem' }}
                        >
                          <option value="">Assign to...</option>
                          {users.map((user) => (
                            <option key={user.id} value={user.id}>
                              {formatUserName(user as any)}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </span>
                </div>

                <div className="task-info-row">
                  <span className="task-info-label">Assigned By:</span>
                  <span className="task-info-value">{getUserName(task.assignedBy)}</span>
                </div>

                <div className="task-info-row">
                  <span className="task-info-label">Created By:</span>
                  <span className="task-info-value">{getUserName(task.createdBy)}</span>
                </div>

                {task.patientId && (
                  <div className="task-info-row">
                    <span className="task-info-label">Patient:</span>
                    <span className="task-info-value">
                      {patient ? (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            navigate(`/patients/${task.patientId}`)
                          }}
                        >
                          {patient.firstName} {patient.lastName}
                        </a>
                      ) : (
                        task.patientId
                      )}
                    </span>
                  </div>
                )}

                {task.ehrComponentType && task.ehrComponentId && (
                  <div className="task-info-row">
                    <span className="task-info-label">EHR Component:</span>
                    <span className="task-info-value">
                      {task.ehrComponentType}: {task.ehrComponentId}
                    </span>
                  </div>
                )}

                {task.dueDate && (
                  <div className="task-info-row">
                    <span className="task-info-label">Due Date:</span>
                    <span
                      className={`task-info-value ${
                        new Date(task.dueDate) < new Date() && task.status !== 'completed'
                          ? 'overdue'
                          : ''
                      }`}
                    >
                      {formatDate(task.dueDate)}
                    </span>
                  </div>
                )}

                <div className="task-info-row">
                  <span className="task-info-label">Created:</span>
                  <span className="task-info-value">{formatDate(task.createdAt)}</span>
                </div>

                {task.completedAt && (
                  <div className="task-info-row">
                    <span className="task-info-label">Completed:</span>
                    <span className="task-info-value">{formatDate(task.completedAt)}</span>
                  </div>
                )}

                {task.resolvedAt && (
                  <div className="task-info-row">
                    <span className="task-info-label">Resolved:</span>
                    <span className="task-info-value">{formatDate(task.resolvedAt)}</span>
                  </div>
                )}

                {task.cancelledAt && (
                  <div className="task-info-row">
                    <span className="task-info-label">Cancelled:</span>
                    <span className="task-info-value">
                      {formatDate(task.cancelledAt)} by {getUserName(task.cancelledBy)}
                    </span>
                  </div>
                )}

                {task.cancelledReason && (
                  <div className="task-info-row">
                    <span className="task-info-label">Cancellation Reason:</span>
                    <span className="task-info-value">{task.cancelledReason}</span>
                  </div>
                )}

                {task.tags && task.tags.length > 0 && (
                  <div className="task-info-row">
                    <span className="task-info-label">Tags:</span>
                    <span className="task-info-value">
                      {task.tags.map((tag, idx) => (
                        <span key={idx} className="task-tag">
                          {tag}
                        </span>
                      ))}
                    </span>
                  </div>
                )}

                {task.isAutomated && (
                  <div className="task-info-row">
                    <span className="task-info-label">Automated:</span>
                    <span className="task-info-value">Yes (Rule: {task.automationRuleId})</span>
                  </div>
                )}
              </div>
            </div>

            <div className="task-detail-section">
              <h3>Status History</h3>
              {task.statusHistory && task.statusHistory.length > 0 ? (
                <div className="task-status-history">
                  {task.statusHistory.map((change, idx) => (
                    <div key={idx} className="task-status-change">
                      <div className="status-change-header">
                        <span className="status-change-status">{change.status}</span>
                        <span className="status-change-date">{formatDate(change.changedAt)}</span>
                      </div>
                      <div className="status-change-details">
                        <span>Changed by: {change.changedByName || change.changedBy}</span>
                        {change.reason && <span>Reason: {change.reason}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No status history available</p>
              )}
            </div>

            {taskAuditEntries.length > 0 && (
              <div className="task-detail-section">
                <h3>Audit Trail</h3>
                <div className="task-audit-trail">
                  {taskAuditEntries.map((entry) => (
                    <div key={entry.id} className="task-audit-entry">
                      <div className="audit-entry-header">
                        <span className="audit-entry-type">{entry.changeType}</span>
                        <span className="audit-entry-date">{formatDate(entry.timestamp)}</span>
                      </div>
                      <div className="audit-entry-details">
                        <span>By: {entry.performedByDisplayName || entry.performedBy}</span>
                        {entry.changes.length > 0 && (
                          <div className="audit-entry-changes">
                            {entry.changes.map((change, idx) => (
                              <div key={idx} className="audit-change">
                                <strong>{change.field}:</strong> {String(change.previousValue)} →{' '}
                                {String(change.newValue)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="task-detail-section">
              <h3>Quick Actions</h3>
              <div className="task-quick-actions">
                {task.status !== 'in-progress' && task.status !== 'completed' && (
                  <button
                    onClick={() => handleStatusChange('in-progress')}
                    className="btn-action"
                  >
                    Start Task
                  </button>
                )}
                <button
                  onClick={() => handleStatusChange('performed')}
                  disabled={task.status === 'performed' || task.status === 'completed'}
                  className="btn-action"
                >
                  Mark Performed
                </button>
                <button
                  onClick={() => handleStatusChange('completed')}
                  disabled={task.status === 'completed'}
                  className="btn-action"
                >
                  Complete Task
                </button>
                <button
                  onClick={() => handleStatusChange('held')}
                  disabled={task.status === 'held' || task.status === 'completed'}
                  className="btn-action"
                >
                  Hold Task
                </button>
                <button
                  onClick={() => handleStatusChange('cancelled')}
                  disabled={task.status === 'cancelled' || task.status === 'completed'}
                  className="btn-action btn-action-danger"
                >
                  Cancel Task
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

